import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { Router, ActivatedRoute } from "@angular/router";

import { UploadDocumentsService } from "../upload-documents/shared/upload-documents.service";
import { ViewStateService } from "./../../../shared/services/view-state.service";
import { UserService } from "./../../../shared/services/user.service";
import { NotificationsService } from "./../../../shared/services/notifications.service";
import { AppSettingsService } from "app/shared/services/app-settings.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-upload-documents",
  templateUrl: "./upload-documents.component.html",
})
export class UploadDocumentsComponent implements OnInit, OnDestroy {
  uploadDocumentForm: FormGroup;
  formLevelErrors: String[] = [];
  documentTypes: any = [];
  fileToUpload = null;
  SaveAndAdd: boolean;
  personId: string = null;
  previousDocument: any;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private uploadService: UploadDocumentsService,
    private viewState: ViewStateService,
    private userService: UserService,
    private formBuilder: FormBuilder,
    private notifications: NotificationsService,
    public appSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.buildForm();
    this.personId = this.route.snapshot.params["personId"];
    this.loadControlData();
    this.addFileReaderPrototype();
    this.getPreviousFiles();
  }

  loadControlData() {
    this.uploadDocumentForm.get("DocumentTypeId").patchValue(13);
    this.viewState.stopLoading();
  }

  getPreviousFiles() {
    this.uploadService
      .getDocuments(this.personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp) => {
          this.previousDocument = resp;
          this.viewState.stopLoading();
        },
        (errors) => {
          this.handleServerErrorResponse(errors);
        }
      );
  }

  addFileReaderPrototype() {
    if (FileReader.prototype.readAsBinaryString === undefined) {
      FileReader.prototype.readAsBinaryString = function (fileData) {
        let binary = "",
          pt = this,
          reader = new FileReader();
        reader.onload = function (e) {
          let bytes = new Uint8Array(reader.result as ArrayBuffer),
            length = bytes.byteLength;
          for (var i = 0; i < length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          pt.content = binary;
          pt.onload();
        };
        reader.readAsArrayBuffer(fileData);
      };
    }
  }

  updateFile(event) {
    this.fileToUpload = event.target.files;
    this.setNonFormValues(event.target.files[0]);
  }

  onSubmit() {
    this.viewState.startLoading();
    this.validateForm();
    if (this.SaveAndAdd) {
      this.uploadFileAndAddAntoher();
    } else {
      this.uploadFile();
    }
    this.SaveAndAdd = false;
  }

  uploadFile() {
    if (this.uploadDocumentForm.valid) {
      this.uploadService
        .uploadNewDocument(this.personId, this.uploadDocumentForm.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.notifications.success("Success", "The document was uploaded.");
            this.router.navigate(["/"]);
          },
          (errors) => {
            this.handleServerErrorResponse(errors);
          }
        );
    }
  }

  uploadFileAndAddAntoher() {
    if (this.uploadDocumentForm.valid) {
      this.uploadService
        .uploadNewDocument(this.personId, this.uploadDocumentForm.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.notifications.success("Success", "The document was uploaded.");
            this.uploadDocumentForm.reset();
            this.loadControlData();
          },
          (errors) => {
            this.handleServerErrorResponse(errors);
          }
        );
    }
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500) {
      this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred. Please try again or contact NBRC for assistance."
      );
    } else {
      this.mapServerErrorsToForm(response.error);
      this.notifications.error(
        "Upload Failed",
        "Please try again or contact NBRC for assistance."
      );
    }
    this.viewState.stopLoading();
  }

  onCancel(event: Event) {
    event.preventDefault();
    this.router.navigate(["/"]);
  }

  setNonFormValues(file: File) {
    this.uploadDocumentForm.patchValue({
      FileData: this.getDataFromFile(file),
      filename: file.name,
      FileType: this.getfriendlyFileType(file.type),
      UploadBy: this.userService.CurrentUser.email,
    });
  }

  getDataFromFile(file: File) {
    let form = this.uploadDocumentForm,
      reader = new FileReader();
    reader.onload = function (readerEvt) {
      if (!readerEvt) {
        form.patchValue({ FileData: btoa(reader["content"]) });
      } else {
        form.patchValue({ FileData: btoa(readerEvt.target["result"] as any) });
      }
    };
    reader.readAsBinaryString(file);
  }

  getfriendlyFileType(type: string): string {
    switch (type) {
      case "application/pdf":
        return "PDF";
      case "application/msword":
        return "DOC";
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        return "DOCX";
      default:
        return "";
    }
  }

  private buildForm(): void {
    this.uploadDocumentForm = this.formBuilder.group({
      File: [this.formValues.File],
      FileData: [this.formValues.FileData],
      DocumentTypeId: [this.formValues.DocumentTypeId, Validators.required],
      FileDescription: [this.formValues.FileDescription],
      filename: [this.formValues.filename],
      UploadBy: [this.formValues.UploadBy],
      FileType: [this.formValues.FileType],
      ExpireDate: [this.formValues.ExpireDate],
    });
  }

  private validateForm() {
    this.formLevelErrors = [];
    let file = this.uploadDocumentForm.get("File");

    if (this.fileToUpload === null) {
      file.setValidators(Validators.required);
    } else {
      file.clearValidators();
    }

    for (const control in this.uploadDocumentForm.controls) {
      this.uploadDocumentForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.uploadDocumentForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }

    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private mapServerErrorsToForm(errors) {
    for (let error of errors) {
      this.formErrors[error.fieldName] = "";
      const control = this.uploadDocumentForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    }
  }

  private formValues = {
    File: "",
    FileData: "",
    DocumentTypeId: "",
    FileDescription: "",
    filename: "",
    UploadBy: "",
    FileType: "",
    ExpireDate: "",
  };

  formErrors = {
    File: "",
    FileData: "",
    DocumentTypeId: "",
    FileDescription: "",
    filename: "",
    UploadBy: "",
    FileType: "",
    ExpireDate: "",
  };

  validationMessages = {
    File: {
      required: "A file is required.",
    },
    DocumentTypeId: {
      required: "A document type is required.",
    },
    FileDescription: {
      letters: "The description",
    },
    ExpireDate: {
      "": "",
    },
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
